/**
 * Art Finder canonical schema, validation, hard filter, soft score, and pick helpers.
 * Works in Node (CommonJS) and browser (globalThis.ArtFinderSchema).
 * Theme asset copy: assets/art-finder-schema.js (keep identical).
 */

(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (root && typeof root === 'object') {
    root.ArtFinderSchema = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const FINDER_GROUPS = [
    'wildlife_presence',
    'western_horses',
    'mountains_open',
    'autumn_golden',
    'desert_earth',
    'night_celestial',
    'water_coastal',
    'quiet_minimal',
  ];

  const SUBJECT_PRIMARY = [
    'wildlife',
    'horse',
    'mountain',
    'forest',
    'desert',
    'architecture',
    'coast',
    'water',
    'night_sky',
    'western',
    'other',
  ];

  const GENRE = [
    'wildlife',
    'western',
    'landscape',
    'nightscape',
    'coastal',
    'desert',
    'alpine',
    'architectural',
    'abstract_nature',
  ];

  const ENVIRONMENT = [
    'alpine',
    'desert',
    'forest',
    'grassland',
    'savannah',
    'coastal',
    'lake',
    'river',
    'urban',
    'rural',
    'western',
    'night',
  ];

  const DAYPART = ['day', 'sunrise', 'sunset', 'blue_hour', 'twilight', 'night'];

  const SEASON = ['spring', 'summer', 'autumn', 'winter', 'neutral'];

  const PALETTE = ['warm', 'cool', 'neutral', 'monochrome', 'earthy', 'colorful', 'dark', 'light'];

  const MOOD = [
    'calm',
    'contemplative',
    'dramatic',
    'powerful',
    'mysterious',
    'adventurous',
    'warm',
    'expansive',
    'intimate',
    'elegant',
    'energetic',
  ];

  const ROOM_FIT = [
    'living_room',
    'bedroom',
    'office',
    'entry',
    'hallway',
    'dining',
    'kitchen',
    'study',
    'mountain_retreat',
    'western_ranch',
    'commercial',
    'large_wall',
  ];

  const INTERIOR_STYLE_FIT = [
    'modern',
    'contemporary',
    'mountain_modern',
    'rustic',
    'traditional',
    'minimalist',
    'luxury',
    'western',
  ];

  const STATEMENT_LEVEL = ['quiet', 'focal', 'dramatic'];

  const RECOMMENDED_MEDIUM = ['metal', 'acrylic', 'framed_acrylic'];

  const ORIENTATION = ['landscape', 'portrait', 'panoramic', 'square'];

  /** Mockup Studio room labels (aligned + Finder-expanded). */
  const ROOM_TYPE_STUDIO = [
    'Modern Living Room',
    'Bedroom',
    'Entryway',
    'Hallway',
    'Dining Room',
    'Gallery Wall',
    'Executive Office',
    'Commercial Space',
    'Kitchen',
    'Study',
    'Mountain Home',
    'Cabin',
    'Lodge',
    'Southwest Interior',
    'Western Farm Ranch',
  ];

  const ROOM_TYPE_TO_FINDER_ROOM_FIT = {
    'Modern Living Room': 'living_room',
    Bedroom: 'bedroom',
    Entryway: 'entry',
    Hallway: 'hallway',
    'Dining Room': 'dining',
    'Gallery Wall': 'large_wall',
    'Executive Office': 'office',
    'Commercial Space': 'commercial',
    Kitchen: 'kitchen',
    Study: 'study',
    'Mountain Home': 'mountain_retreat',
    Cabin: 'mountain_retreat',
    Lodge: 'mountain_retreat',
    'Southwest Interior': 'living_room',
    'Western Farm Ranch': 'western_ranch',
  };

  const FINDER_METAFIELD_KEYS = [
    { key: 'finder_enabled', type: 'boolean', name: 'Finder enabled' },
    { key: 'finder_subject_primary', type: 'single_line_text_field', name: 'Finder subject primary', choices: SUBJECT_PRIMARY },
    { key: 'finder_subject_secondary', type: 'list.single_line_text_field', name: 'Finder subject secondary', choices: SUBJECT_PRIMARY },
    { key: 'finder_genre', type: 'single_line_text_field', name: 'Finder genre', choices: GENRE },
    { key: 'finder_environment', type: 'list.single_line_text_field', name: 'Finder environment', choices: ENVIRONMENT },
    { key: 'finder_daypart', type: 'single_line_text_field', name: 'Finder daypart', choices: DAYPART },
    { key: 'finder_night_sky', type: 'boolean', name: 'Finder night sky' },
    { key: 'finder_season', type: 'single_line_text_field', name: 'Finder season', choices: SEASON },
    { key: 'finder_palette', type: 'list.single_line_text_field', name: 'Finder palette', choices: PALETTE },
    { key: 'finder_brightness', type: 'number_integer', name: 'Finder brightness' },
    { key: 'finder_contrast', type: 'number_integer', name: 'Finder contrast' },
    { key: 'finder_mood', type: 'list.single_line_text_field', name: 'Finder mood', choices: MOOD },
    { key: 'finder_energy', type: 'number_integer', name: 'Finder energy' },
    { key: 'finder_visual_density', type: 'number_integer', name: 'Finder visual density' },
    { key: 'finder_orientation', type: 'single_line_text_field', name: 'Finder orientation', choices: ORIENTATION },
    { key: 'finder_room_fit', type: 'list.single_line_text_field', name: 'Finder room fit', choices: ROOM_FIT },
    { key: 'finder_interior_style_fit', type: 'list.single_line_text_field', name: 'Finder interior style fit', choices: INTERIOR_STYLE_FIT },
    { key: 'finder_statement_level', type: 'single_line_text_field', name: 'Finder statement level', choices: STATEMENT_LEVEL },
    { key: 'finder_recommended_medium', type: 'list.single_line_text_field', name: 'Finder recommended medium', choices: RECOMMENDED_MEDIUM },
    { key: 'finder_exclusions', type: 'list.single_line_text_field', name: 'Finder exclusions', choices: FINDER_GROUPS },
    { key: 'finder_featured_score', type: 'number_decimal', name: 'Finder featured score' },
    { key: 'finder_groups', type: 'list.single_line_text_field', name: 'Finder groups', choices: FINDER_GROUPS },
  ];

  const SOFT_WEIGHTS = {
    room: 28,
    mood: 25,
    statement: 12,
    subject: 12,
    palette: 10,
    interior: 5,
    featured: 5,
  };

  const STATEMENT_RANK = { quiet: 0, focal: 1, dramatic: 2 };

  const QUIZ_SUBJECT_TO_GROUP = {
    wildlife: 'wildlife_presence',
    wildlife_presence: 'wildlife_presence',
    horses: 'western_horses',
    western: 'western_horses',
    western_horses: 'western_horses',
    mountains: 'mountains_open',
    mountains_open: 'mountains_open',
    autumn: 'autumn_golden',
    autumn_golden: 'autumn_golden',
    desert: 'desert_earth',
    desert_earth: 'desert_earth',
    night: 'night_celestial',
    night_sky: 'night_celestial',
    night_celestial: 'night_celestial',
    water: 'water_coastal',
    coastal: 'water_coastal',
    water_coastal: 'water_coastal',
    quiet: 'quiet_minimal',
    quiet_minimal: 'quiet_minimal',
    surprise: null,
    surprise_me: null,
  };

  const QUIZ_MOOD_MAP = {
    calm: ['calm', 'contemplative', 'intimate'],
    peaceful: ['calm', 'contemplative'],
    dramatic: ['dramatic', 'powerful', 'energetic'],
    warm: ['warm', 'calm'],
    warm_inviting: ['warm', 'calm', 'intimate'],
    adventurous: ['adventurous', 'expansive', 'energetic'],
    powerful: ['powerful', 'dramatic'],
    mysterious: ['mysterious', 'contemplative'],
    sophisticated: ['elegant', 'contemplative'],
    inspiring: ['expansive', 'energetic', 'warm'],
    elegant: ['elegant', 'calm'],
  };

  const QUIZ_PALETTE_MAP = {
    warm: ['warm', 'earthy', 'colorful'],
    warm_earthy: ['warm', 'earthy'],
    cool: ['cool', 'light'],
    cool_blues: ['cool'],
    dark: ['dark', 'cool', 'monochrome'],
    dark_moody: ['dark', 'cool', 'monochrome'],
    light: ['light', 'neutral'],
    light_airy: ['light', 'neutral', 'cool'],
    mono: ['monochrome', 'neutral'],
    monochrome: ['monochrome', 'neutral'],
    neutral_monochrome: ['monochrome', 'neutral'],
    earth: ['earthy', 'warm'],
    earthy: ['earthy', 'warm'],
    colorful: ['colorful', 'warm'],
    bold: ['colorful', 'dark', 'warm'],
  };

  /** Quiz palettes that must not return B&W / monochrome-only prints. */
  const QUIZ_PALETTE_EXCLUDE_MONOCHROME_ONLY = new Set([
    'colorful',
    'warm',
    'warm_earthy',
    'earthy',
    'earth',
    'bold',
  ]);

  const QUIZ_ROOM_MAP = {
    living: 'living_room',
    living_room: 'living_room',
    bedroom: 'bedroom',
    office: 'office',
    entry: 'entry',
    entryway: 'entry',
    hallway: 'hallway',
    dining: 'dining',
    kitchen: 'kitchen',
    study: 'study',
    mountain: 'mountain_retreat',
    mountain_retreat: 'mountain_retreat',
    western_ranch: 'western_ranch',
    ranch: 'western_ranch',
    commercial: 'commercial',
    feature: 'large_wall',
    large_wall: 'large_wall',
    gallery: 'large_wall',
    other: null,
  };

  const QUIZ_STATEMENT_MAP = {
    quiet: 'quiet',
    quiet_complement: 'quiet',
    focal: 'focal',
    clear_focal: 'focal',
    clear_focal_point: 'focal',
    dramatic: 'dramatic',
    dramatic_statement: 'dramatic',
  };

  function asArray(value) {
    if (value == null || value === '') return [];
    if (Array.isArray(value)) return value.map(String).filter(Boolean);
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [String(parsed)];
        } catch {
          return [trimmed];
        }
      }
      return [trimmed];
    }
    return [String(value)];
  }

  function asBool(value) {
    if (typeof value === 'boolean') return value;
    if (value == null || value === '') return null;
    const s = String(value).toLowerCase();
    if (s === 'true' || s === '1') return true;
    if (s === 'false' || s === '0') return false;
    return null;
  }

  function asNumber(value) {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  function field(record, shortKey) {
    if (!record || typeof record !== 'object') return undefined;
    if (Object.prototype.hasOwnProperty.call(record, shortKey)) return record[shortKey];
    const finderKey = shortKey.startsWith('finder_') ? shortKey : `finder_${shortKey}`;
    if (Object.prototype.hasOwnProperty.call(record, finderKey)) return record[finderKey];
    const bare = shortKey.replace(/^finder_/, '');
    if (Object.prototype.hasOwnProperty.call(record, bare)) return record[bare];
    return undefined;
  }

  function normalizeProductMeta(raw) {
    const enabled = asBool(field(raw, 'enabled'));
    return {
      enabled: enabled == null ? asBool(field(raw, 'finder_enabled')) : enabled,
      subject_primary: field(raw, 'subject_primary') != null ? String(field(raw, 'subject_primary')) : '',
      subject_secondary: asArray(field(raw, 'subject_secondary')),
      genre: field(raw, 'genre') != null ? String(field(raw, 'genre')) : '',
      environment: asArray(field(raw, 'environment')),
      daypart: field(raw, 'daypart') != null ? String(field(raw, 'daypart')) : '',
      night_sky: asBool(field(raw, 'night_sky')),
      season: field(raw, 'season') != null ? String(field(raw, 'season')) : '',
      palette: asArray(field(raw, 'palette')),
      brightness: asNumber(field(raw, 'brightness')),
      contrast: asNumber(field(raw, 'contrast')),
      mood: asArray(field(raw, 'mood')),
      energy: asNumber(field(raw, 'energy')),
      visual_density: asNumber(field(raw, 'visual_density')),
      orientation: field(raw, 'orientation') != null ? String(field(raw, 'orientation')) : '',
      room_fit: asArray(field(raw, 'room_fit')),
      interior_style_fit: asArray(field(raw, 'interior_style_fit')),
      statement_level: field(raw, 'statement_level') != null ? String(field(raw, 'statement_level')) : '',
      recommended_medium: asArray(field(raw, 'recommended_medium')),
      exclusions: asArray(field(raw, 'exclusions')),
      featured_score: asNumber(field(raw, 'featured_score')),
      groups: asArray(field(raw, 'groups')),
      handle: raw && raw.handle ? String(raw.handle) : '',
      title: raw && raw.title ? String(raw.title) : '',
    };
  }

  function roomTypeToFinderRoomFit(roomType) {
    if (roomType == null || roomType === '') return null;
    const key = String(roomType).trim();
    if (ROOM_TYPE_TO_FINDER_ROOM_FIT[key]) return ROOM_TYPE_TO_FINDER_ROOM_FIT[key];
    const lower = key.toLowerCase();
    for (const [studio, fit] of Object.entries(ROOM_TYPE_TO_FINDER_ROOM_FIT)) {
      if (studio.toLowerCase() === lower) return fit;
    }
    return null;
  }

  function minimumFinderFields() {
    return [
      'finder_enabled',
      'finder_subject_primary',
      'finder_genre',
      'finder_daypart',
      'finder_night_sky',
      'finder_season',
      'finder_palette',
      'finder_mood',
      'finder_room_fit',
      'finder_statement_level',
      'finder_groups',
    ];
  }

  function inEnum(value, list) {
    return list.includes(String(value));
  }

  function validateEnum(errors, warnings, label, value, list, { required = false, listField = false } = {}) {
    if (listField) {
      const values = asArray(value);
      if (required && !values.length) {
        errors.push(`${label} is required`);
        return;
      }
      for (const v of values) {
        if (!inEnum(v, list)) errors.push(`${label} has invalid value: ${v}`);
      }
      return;
    }
    if (value == null || value === '') {
      if (required) errors.push(`${label} is required`);
      return;
    }
    if (!inEnum(value, list)) errors.push(`${label} has invalid value: ${value}`);
  }

  function validateScale(errors, warnings, label, value) {
    if (value == null || value === '') return;
    const n = asNumber(value);
    if (n == null) {
      errors.push(`${label} must be a number`);
      return;
    }
    if (n < 1 || n > 5) warnings.push(`${label} expected 1–5, got ${n}`);
  }

  function validateFinderRecord(record) {
    const errors = [];
    const warnings = [];
    const meta = normalizeProductMeta(record || {});

    const enabled = meta.enabled;
    if (enabled == null) {
      errors.push('finder_enabled is required');
    }

    if (enabled === true) {
      validateEnum(errors, warnings, 'finder_subject_primary', meta.subject_primary, SUBJECT_PRIMARY, { required: true });
      validateEnum(errors, warnings, 'finder_genre', meta.genre, GENRE, { required: true });
      validateEnum(errors, warnings, 'finder_daypart', meta.daypart, DAYPART, { required: true });
      if (meta.night_sky == null) errors.push('finder_night_sky is required');
      validateEnum(errors, warnings, 'finder_season', meta.season, SEASON, { required: true });
      validateEnum(errors, warnings, 'finder_palette', meta.palette, PALETTE, { required: true, listField: true });
      validateEnum(errors, warnings, 'finder_mood', meta.mood, MOOD, { required: true, listField: true });
      validateEnum(errors, warnings, 'finder_room_fit', meta.room_fit, ROOM_FIT, { required: true, listField: true });
      validateEnum(errors, warnings, 'finder_statement_level', meta.statement_level, STATEMENT_LEVEL, { required: true });
      validateEnum(errors, warnings, 'finder_groups', meta.groups, FINDER_GROUPS, { required: true, listField: true });
    } else {
      if (meta.subject_primary) validateEnum(errors, warnings, 'finder_subject_primary', meta.subject_primary, SUBJECT_PRIMARY);
      if (meta.genre) validateEnum(errors, warnings, 'finder_genre', meta.genre, GENRE);
      if (meta.daypart) validateEnum(errors, warnings, 'finder_daypart', meta.daypart, DAYPART);
      if (meta.season) validateEnum(errors, warnings, 'finder_season', meta.season, SEASON);
      if (meta.statement_level) {
        validateEnum(errors, warnings, 'finder_statement_level', meta.statement_level, STATEMENT_LEVEL);
      }
      validateEnum(errors, warnings, 'finder_groups', meta.groups, FINDER_GROUPS, { listField: true });
      validateEnum(errors, warnings, 'finder_palette', meta.palette, PALETTE, { listField: true });
      validateEnum(errors, warnings, 'finder_mood', meta.mood, MOOD, { listField: true });
      validateEnum(errors, warnings, 'finder_room_fit', meta.room_fit, ROOM_FIT, { listField: true });
    }

    validateEnum(errors, warnings, 'finder_subject_secondary', meta.subject_secondary, SUBJECT_PRIMARY, { listField: true });
    validateEnum(errors, warnings, 'finder_environment', meta.environment, ENVIRONMENT, { listField: true });
    validateEnum(errors, warnings, 'finder_interior_style_fit', meta.interior_style_fit, INTERIOR_STYLE_FIT, {
      listField: true,
    });
    validateEnum(errors, warnings, 'finder_recommended_medium', meta.recommended_medium, RECOMMENDED_MEDIUM, {
      listField: true,
    });
    validateEnum(errors, warnings, 'finder_exclusions', meta.exclusions, FINDER_GROUPS, { listField: true });
    if (meta.orientation) validateEnum(errors, warnings, 'finder_orientation', meta.orientation, ORIENTATION);

    validateScale(errors, warnings, 'finder_brightness', meta.brightness);
    validateScale(errors, warnings, 'finder_contrast', meta.contrast);
    validateScale(errors, warnings, 'finder_energy', meta.energy);
    validateScale(errors, warnings, 'finder_visual_density', meta.visual_density);

    if (meta.featured_score != null && (meta.featured_score < 0 || meta.featured_score > 10)) {
      warnings.push(`finder_featured_score expected 0–10, got ${meta.featured_score}`);
    }

    // Consistency warnings
    if (meta.night_sky === true && meta.daypart && !['night', 'blue_hour', 'twilight'].includes(meta.daypart)) {
      warnings.push('finder_night_sky true with non-night daypart');
    }
    if (meta.groups.includes('night_celestial') && meta.night_sky === false && meta.daypart !== 'night' && meta.daypart !== 'blue_hour') {
      warnings.push('night_celestial group without night_sky or night/blue_hour daypart');
    }
    if (meta.groups.includes('autumn_golden') && meta.season && meta.season !== 'autumn') {
      warnings.push('autumn_golden group without autumn season');
    }
    if (meta.exclusions.some((g) => meta.groups.includes(g))) {
      warnings.push('finder_exclusions overlaps finder_groups');
    }
    if (
      (meta.subject_primary === 'horse' || meta.genre === 'western') &&
      meta.groups.includes('night_celestial') &&
      !meta.groups.includes('western_horses')
    ) {
      warnings.push('horse/western subject in night_celestial without western_horses group');
    }

    return { ok: errors.length === 0, errors, warnings };
  }

  function resolveQuizSubjectGroup(quizAnswers) {
    const raw =
      quizAnswers?.subject_group ||
      quizAnswers?.subject ||
      quizAnswers?.scenes ||
      quizAnswers?.group ||
      '';
    const key = String(raw || '').trim().toLowerCase().replace(/\s+/g, '_');
    if (!key) return undefined;
    if (Object.prototype.hasOwnProperty.call(QUIZ_SUBJECT_TO_GROUP, key)) {
      return QUIZ_SUBJECT_TO_GROUP[key];
    }
    if (FINDER_GROUPS.includes(key)) return key;
    return key;
  }

  function isNightSkyArtwork(meta) {
    return (
      meta.night_sky === true ||
      meta.subject_primary === 'night_sky' ||
      meta.genre === 'nightscape'
    );
  }

  function productMatchesSubjectGroup(meta, group) {
    if (!group) return true;
    if (meta.exclusions.includes(group)) return false;
    if (group === 'night_celestial') {
      return isNightSkyArtwork(meta);
    }
    if (meta.groups.includes(group)) return true;

    switch (group) {
      case 'night_celestial':
        return isNightSkyArtwork(meta);
      case 'western_horses':
        return (
          meta.subject_primary === 'horse' ||
          meta.subject_primary === 'western' ||
          meta.genre === 'western' ||
          meta.subject_secondary.includes('horse') ||
          meta.subject_secondary.includes('western') ||
          meta.environment.includes('western')
        );
      case 'wildlife_presence':
        return (
          meta.subject_primary === 'wildlife' ||
          meta.genre === 'wildlife' ||
          meta.subject_secondary.includes('wildlife')
        );
      case 'autumn_golden':
        return meta.season === 'autumn' || meta.subject_primary === 'forest';
      case 'mountains_open':
        return (
          meta.subject_primary === 'mountain' ||
          meta.genre === 'alpine' ||
          meta.environment.includes('alpine')
        );
      case 'desert_earth':
        return (
          meta.subject_primary === 'desert' ||
          meta.genre === 'desert' ||
          meta.environment.includes('desert')
        );
      case 'water_coastal':
        return (
          meta.subject_primary === 'coast' ||
          meta.subject_primary === 'water' ||
          meta.genre === 'coastal' ||
          meta.environment.includes('coastal') ||
          meta.environment.includes('lake') ||
          meta.environment.includes('river')
        );
      case 'quiet_minimal':
        return meta.statement_level === 'quiet' || meta.mood.includes('calm') || meta.mood.includes('contemplative');
      default:
        return false;
    }
  }

  function hardFilterEligible(productMeta, quizAnswers) {
    const meta = normalizeProductMeta(productMeta);
    if (meta.enabled !== true) return false;

    const quiz = quizAnswers || {};
    if (!paletteHardEligible(meta, quiz)) return false;

    const group = resolveQuizSubjectGroup(quiz);
    // surprise / null group → skip subject hard filter
    if (group == null) return true;
    return productMatchesSubjectGroup(meta, group);
  }

  /** True when palette is B&W/neutral only — no warm/cool/earthy/colorful signal. */
  function isMonochromeOnlyPalette(meta) {
    const p = new Set(asArray(meta.palette).map((v) => String(v).toLowerCase()));
    if (!p.has('monochrome')) return false;
    return !['warm', 'cool', 'earthy', 'colorful'].some((c) => p.has(c));
  }

  function resolveQuizPaletteKey(quizAnswers) {
    const quiz = quizAnswers || {};
    return String(quiz.palette || quiz.visual || quiz.visual_character || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
  }

  function paletteHardEligible(meta, quizAnswers) {
    const paletteKey = resolveQuizPaletteKey(quizAnswers);
    if (!paletteKey || !QUIZ_PALETTE_EXCLUDE_MONOCHROME_ONLY.has(paletteKey)) return true;
    return !isMonochromeOnlyPalette(meta);
  }

  function overlapScore(productValues, wanted, maxPoints) {
    const prod = new Set(asArray(productValues).map((v) => String(v).toLowerCase()));
    const want = asArray(wanted).map((v) => String(v).toLowerCase()).filter(Boolean);
    if (!want.length || !prod.size) return { points: 0, matched: [] };
    const matched = want.filter((w) => prod.has(w));
    if (!matched.length) return { points: 0, matched: [] };
    const ratio = matched.length / want.length;
    return { points: Math.round(maxPoints * ratio * 1000) / 1000, matched };
  }

  function softScore(productMeta, quizAnswers) {
    const meta = normalizeProductMeta(productMeta);
    const quiz = quizAnswers || {};
    const reasons = [];
    let score = 0;

    const group = resolveQuizSubjectGroup(quiz);
    if (group) {
      if (meta.groups.includes(group)) {
        score += SOFT_WEIGHTS.subject;
        reasons.push({ field: 'subject', weight: SOFT_WEIGHTS.subject, detail: group });
      } else if (productMatchesSubjectGroup(meta, group)) {
        const partial = Math.round(SOFT_WEIGHTS.subject * 0.7 * 1000) / 1000;
        score += partial;
        reasons.push({ field: 'subject', weight: partial, detail: `${group}:partial` });
      }
    } else {
      // Surprise me: slight boost for featured works
      const featured = Math.min(SOFT_WEIGHTS.subject, (meta.featured_score || 0) * 3);
      if (featured > 0) {
        score += featured;
        reasons.push({ field: 'subject', weight: featured, detail: 'surprise:featured' });
      }
    }

    const moodKey = String(quiz.mood || quiz.feeling || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
    const wantedMoods = QUIZ_MOOD_MAP[moodKey] || (moodKey ? [moodKey] : []);
    const moodHit = overlapScore(meta.mood, wantedMoods, SOFT_WEIGHTS.mood);
    score += moodHit.points;
    if (moodHit.matched.length) {
      reasons.push({ field: 'mood', weight: moodHit.points, detail: moodHit.matched.join(',') });
    }

    const paletteKey = resolveQuizPaletteKey(quiz);
    const wantedPalette = QUIZ_PALETTE_MAP[paletteKey] || (paletteKey ? [paletteKey] : []);
    const paletteHit = overlapScore(meta.palette, wantedPalette, SOFT_WEIGHTS.palette);
    score += paletteHit.points;
    if (paletteHit.matched.length) {
      reasons.push({ field: 'palette', weight: paletteHit.points, detail: paletteHit.matched.join(',') });
    }

    const roomKey = String(quiz.room || quiz.space || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
    const wantedRoom = QUIZ_ROOM_MAP[roomKey] || (ROOM_FIT.includes(roomKey) ? roomKey : null);
    if (wantedRoom && meta.room_fit.includes(wantedRoom)) {
      score += SOFT_WEIGHTS.room;
      reasons.push({ field: 'room', weight: SOFT_WEIGHTS.room, detail: wantedRoom });
    }

    const statementKey = String(quiz.statement || quiz.presence || quiz.statement_level || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
    const wantedStatement = QUIZ_STATEMENT_MAP[statementKey] || (STATEMENT_LEVEL.includes(statementKey) ? statementKey : null);
    if (wantedStatement && meta.statement_level === wantedStatement) {
      score += SOFT_WEIGHTS.statement;
      reasons.push({ field: 'statement', weight: SOFT_WEIGHTS.statement, detail: wantedStatement });
    } else if (wantedStatement && meta.statement_level) {
      const dist = Math.abs((STATEMENT_RANK[meta.statement_level] ?? 1) - (STATEMENT_RANK[wantedStatement] ?? 1));
      if (dist === 1) {
        const partial = Math.round(SOFT_WEIGHTS.statement * 0.4 * 1000) / 1000;
        score += partial;
        reasons.push({ field: 'statement', weight: partial, detail: `${meta.statement_level}:near` });
      }
    }

    const interiorKey = String(quiz.interior_style || quiz.room_style || quiz.style || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
    if (interiorKey && meta.interior_style_fit.includes(interiorKey)) {
      score += SOFT_WEIGHTS.interior;
      reasons.push({ field: 'interior', weight: SOFT_WEIGHTS.interior, detail: interiorKey });
    }

    const featured = meta.featured_score == null ? 0 : Math.max(0, Math.min(10, meta.featured_score));
    const featuredPoints = Math.round((featured / 10) * SOFT_WEIGHTS.featured * 1000) / 1000;
    if (featuredPoints > 0) {
      score += featuredPoints;
      reasons.push({ field: 'featured', weight: featuredPoints, detail: String(featured) });
    }

    return {
      score: Math.round(score * 1000) / 1000,
      matchReasons: reasons,
      meta,
    };
  }

  function statementRank(level) {
    return STATEMENT_RANK[level] ?? 1;
  }

  function sortByScoreThenHandle(a, b) {
    if (b.score !== a.score) return b.score - a.score;
    return String(a.handle).localeCompare(String(b.handle));
  }

  function pickFromBand(candidates, preferLowerStatement) {
    if (!candidates.length) return null;
    return candidates
      .slice()
      .sort((a, b) => {
        const sa = statementRank(a.statement_level);
        const sb = statementRank(b.statement_level);
        if (preferLowerStatement) {
          if (sa !== sb) return sa - sb;
          if (a.energy !== b.energy) return a.energy - b.energy;
        } else {
          if (sa !== sb) return sb - sa;
          if (a.energy !== b.energy) return b.energy - a.energy;
        }
        return sortByScoreThenHandle(a, b);
      })[0];
  }

  /**
   * Mid-score exploration pick: skip the top ~30% of remaining scores so
   * quieter/bolder are not just #2/#3 by soft score when statement is monoculture.
   */
  function midBandExploration(list, used) {
    const remaining = list.filter((r) => !used.has(r.handle));
    if (!remaining.length) return null;
    if (remaining.length === 1) return remaining[0];
    const sorted = remaining.slice().sort(sortByScoreThenHandle);
    const skip = Math.max(1, Math.floor(sorted.length * 0.3));
    const band = sorted.slice(skip);
    if (!band.length) return sorted[sorted.length - 1];
    return band[Math.floor(band.length / 2)];
  }

  function pickRecommendations(eligibleScored) {
    const list = (eligibleScored || [])
      .map((row) => {
        const score = typeof row.score === 'number' ? row.score : softScore(row, row.quiz || {}).score;
        const meta = row.meta || normalizeProductMeta(row.product || row);
        return {
          ...row,
          score,
          meta,
          statement_level: meta.statement_level || row.statement_level || 'focal',
          energy: meta.energy != null ? meta.energy : row.energy != null ? row.energy : 3,
          handle: row.handle || meta.handle || '',
        };
      })
      .sort(sortByScoreThenHandle);

    const strongest = list[0] || null;
    const used = new Set(strongest ? [strongest.handle] : []);
    if (!strongest) return { strongest: null, quieter: null, bolder: null };

    const strongRank = statementRank(strongest.statement_level);
    const remaining = () => list.filter((r) => !used.has(r.handle));

    // Quieter: prefer strictly lower statement than strongest; else mid-band exploration soft-floor.
    const quieterLower = remaining().filter((r) => statementRank(r.statement_level) < strongRank);
    let quieter = quieterLower.length
      ? pickFromBand(quieterLower, true)
      : midBandExploration(list, used);
    if (quieter) used.add(quieter.handle);

    // Bolder: prefer strictly higher statement than strongest; else highest energy among leftover,
    // falling back to mid-band if pool still monoculture at top.
    const bolderHigher = remaining().filter((r) => statementRank(r.statement_level) > strongRank);
    let bolder = null;
    if (bolderHigher.length) {
      bolder = pickFromBand(bolderHigher, false);
    } else {
      const rest = remaining();
      const distinctStatement = new Set(rest.map((r) => r.statement_level));
      if (distinctStatement.size <= 1 && rest.length >= 2) {
        bolder = midBandExploration(list, used);
        // Avoid picking the same mid slot as quieter when possible
        if (bolder && quieter && bolder.handle === quieter.handle) {
          const alt = rest.filter((r) => r.handle !== quieter.handle).sort(sortByScoreThenHandle);
          bolder = alt[Math.floor(alt.length / 2)] || alt[0] || null;
        }
      } else {
        bolder = pickFromBand(rest, false);
      }
    }
    if (bolder) used.add(bolder.handle);

    // Soft-floor: if pool is large and quieter+bolder are both still in the top-3 by score,
    // replace quieter with a mid-band exploration card so one slot surfaces mid-pack inventory.
    if (list.length >= 5 && quieter && bolder) {
      const top3 = new Set(list.slice(0, 3).map((r) => r.handle));
      if (top3.has(quieter.handle) && top3.has(bolder.handle)) {
        const explore = midBandExploration(list, new Set([strongest.handle, bolder.handle]));
        if (explore && explore.handle !== quieter.handle) {
          quieter = explore;
        }
      }
    }

    return { strongest, quieter, bolder };
  }

  const SUBJECT_LABELS = {
    wildlife_presence: 'wildlife presence',
    western_horses: 'western horses',
    mountains_open: 'open mountain country',
    autumn_golden: 'autumn light',
    desert_earth: 'desert form',
    night_celestial: 'night sky',
    water_coastal: 'water and coast',
    quiet_minimal: 'quiet presence',
  };

  function whyCopy(product, quiz, matchReasons) {
    const meta = normalizeProductMeta(product || {});
    const quizObj = quiz || {};
    const reasons = Array.isArray(matchReasons) ? matchReasons.slice() : softScore(meta, quizObj).matchReasons;
    const byField = {};
    for (const r of reasons) {
      if (!r || r.weight <= 0) continue;
      if (!byField[r.field] || r.weight > byField[r.field].weight) byField[r.field] = r;
    }

    const roomKey = String(quizObj.room || quizObj.space || (byField.room && byField.room.detail) || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
    const roomLabel = roomKey ? roomKey.replace(/_/g, ' ') : '';
    const moodDetail = byField.mood ? String(byField.mood.detail).replace(/,/g, '/') : '';
    const paletteDetail = byField.palette ? String(byField.palette.detail).replace(/,/g, ' and ') : '';
    const statementDetail = byField.statement
      ? String(byField.statement.detail).replace(/:near$/, '').replace(/_/g, ' ')
      : '';

    const sentences = [];
    if (paletteDetail && roomLabel) {
      sentences.push(`The ${paletteDetail} palette fits a ${roomLabel} without taking over the room.`);
    } else if (moodDetail && roomLabel) {
      sentences.push(`It brings a ${moodDetail} feel that belongs in a ${roomLabel}.`);
    } else if (moodDetail) {
      sentences.push(`It carries the ${moodDetail} feeling you chose.`);
    } else if (roomLabel) {
      sentences.push(`It is a strong fit for a ${roomLabel}.`);
    }

    if (roomKey === 'living_room') {
      sentences.push('The composition sits well above a sofa or console.');
    } else if (statementDetail && sentences.length < 2) {
      sentences.push(`It lands as a ${statementDetail} on the wall.`);
    } else if (paletteDetail && !roomLabel && sentences.length < 2) {
      sentences.push(`The ${paletteDetail} palette sits easily in the room.`);
    }

    if (!sentences.length) {
      const title = meta.title || product?.title || 'This artwork';
      return `${title} is a close fit for the answers you gave.`;
    }
    return sentences.slice(0, 2).join(' ');
  }

  function metafieldValueFromSeed(def, value) {
    if (value == null || value === '') return null;
    if (def.type === 'boolean') {
      const b = asBool(value);
      if (b == null) return null;
      return b ? 'true' : 'false';
    }
    if (def.type === 'number_integer') {
      const n = asNumber(value);
      return n == null ? null : String(Math.round(n));
    }
    if (def.type === 'number_decimal') {
      const n = asNumber(value);
      return n == null ? null : String(n);
    }
    if (def.type.startsWith('list.')) {
      return JSON.stringify(asArray(value));
    }
    return String(value);
  }

  function seedRecordToMetafields(ownerId, record) {
    const out = [];
    for (const def of FINDER_METAFIELD_KEYS) {
      const short = def.key.replace(/^finder_/, '');
      let value = record[def.key];
      if (value === undefined) value = record[short];
      const serialized = metafieldValueFromSeed(def, value);
      if (serialized == null) continue;
      out.push({
        ownerId,
        namespace: 'custom',
        key: def.key,
        type: def.type,
        value: serialized,
      });
    }
    return out;
  }

  return {
    FINDER_GROUPS,
    SUBJECT_PRIMARY,
    GENRE,
    ENVIRONMENT,
    DAYPART,
    SEASON,
    PALETTE,
    MOOD,
    ROOM_FIT,
    INTERIOR_STYLE_FIT,
    STATEMENT_LEVEL,
    RECOMMENDED_MEDIUM,
    ORIENTATION,
    ROOM_TYPE_STUDIO,
    ROOM_TYPE_TO_FINDER_ROOM_FIT,
    FINDER_METAFIELD_KEYS,
    SOFT_WEIGHTS,
    roomTypeToFinderRoomFit,
    minimumFinderFields,
    validateFinderRecord,
    hardFilterEligible,
    softScore,
    pickRecommendations,
    whyCopy,
    normalizeProductMeta,
    seedRecordToMetafields,
    metafieldValueFromSeed,
  };
});
